import { c as _c } from "react/compiler-runtime";
import { Field, FieldContent, FieldDescription, FieldError, FieldLabel, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, selectVariant, Spinner } from '@repo/ui/components';
import { ArrowLeftRight, Building, Caravan, Factory, type LucideIcon } from '@repo/ui/icons';
import { cn, VariantProps } from '@repo/ui/lib/utils';
import { ComponentProps, ReactNode } from 'react';
import { ReadOnlyField, useReadOnly } from '../../components/readonly.tsx';
import { useFieldContext } from '../../lib/form-context.ts';
type CompanyTypeOption = {
  icon?: LucideIcon;
  label: string;
  value: string;
};
const COMPANY_TYPE_OPTIONS: readonly CompanyTypeOption[] = [{
  label: 'Customer',
  value: 'shipper'
}, {
  label: 'Carrier',
  value: 'haulier'
}, {
  label: 'Broker',
  value: 'broker'
}, {
  label: 'Other',
  value: 'other'
}];
const COMPANY_TYPE_ICONS = {
  broker: ArrowLeftRight,
  haulier: Caravan,
  other: Building,
  shipper: Factory
} as const;
const isCompanyTypeValue = (value: string): value is keyof typeof COMPANY_TYPE_ICONS => Object.hasOwn(COMPANY_TYPE_ICONS, value);
const CompanyTypeOptionLabel = t0 => {
  const $ = _c(5);
  const {
    icon: IconOverride,
    label,
    value
  } = t0;
  const Icon = IconOverride ?? (isCompanyTypeValue(value) ? COMPANY_TYPE_ICONS[value] : null);
  let t1;
  if ($[0] !== Icon) {
    t1 = Icon ? <Icon aria-hidden={true} className="text-muted-foreground size-4 shrink-0" /> : null;
    $[0] = Icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label || $[3] !== t1) {
    t2 = <span className="flex min-w-0 items-center gap-2" data-sentry-component="CompanyTypeOptionLabel" data-sentry-source-file="form-company-type-select.tsx">{t1}{label}</span>;
    $[2] = label;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export const FormCompanyTypeSelect = t0 => {
  const $ = _c(99);
  const {
    allowedValues,
    "data-testid": dataTestId,
    description,
    isPending: t1,
    label,
    options,
    placeholder: t2,
    required,
    variant: t3
  } = t0;
  const isPending = t1 === undefined ? false : t1;
  const placeholder = t2 === undefined ? "-" : t2;
  const variant = t3 === undefined ? "default" : t3;
  const field = useFieldContext();
  const isReadOnly = useReadOnly();
  const isCompact = variant === "ghost" || variant === "no-style";
  const allOptions = options ?? COMPANY_TYPE_OPTIONS;
  let T0;
  let T1;
  let T2;
  let T3;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== allOptions || $[1] !== allowedValues || $[2] !== dataTestId || $[3] !== field || $[4] !== isCompact || $[5] !== isPending || $[6] !== isReadOnly || $[7] !== label || $[8] !== placeholder || $[9] !== required || $[10] !== variant) {
    t22 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t23;
      if ($[34] !== allowedValues) {
        t23 = option => !allowedValues || allowedValues.includes(option.value);
        $[34] = allowedValues;
        $[35] = t23;
      } else {
        t23 = $[35];
      }
      const resolvedOptions = allOptions.filter(t23);
      let t24;
      if ($[36] !== field || $[37] !== label || $[38] !== required) {
        t24 = label && typeof label === "string" ? <FieldLabel className={cn(required && "required gap-0.5")} htmlFor={field.name}>{label}</FieldLabel> : label;
        $[36] = field;
        $[37] = label;
        $[38] = required;
        $[39] = t24;
      } else {
        t24 = $[39];
      }
      const fieldLabel = t24;
      if (isReadOnly) {
        let t25;
        if ($[40] !== allOptions || $[41] !== field) {
          t25 = allOptions.find(option_0 => option_0.value === field.state.value)?.label ?? "-";
          $[40] = allOptions;
          $[41] = field;
          $[42] = t25;
        } else {
          t25 = $[42];
        }
        let t26;
        if ($[43] !== fieldLabel || $[44] !== t25) {
          t26 = <ReadOnlyField label={fieldLabel}>{t25}</ReadOnlyField>;
          $[43] = fieldLabel;
          $[44] = t25;
          $[45] = t26;
        } else {
          t26 = $[45];
        }
        t22 = t26;
        break bb0;
      }
      T3 = Field;
      t18 = "gap-1";
      t19 = "Field";
      t20 = "FormCompanyTypeSelect";
      t21 = "form-company-type-select.tsx";
      T2 = FieldContent;
      t15 = "FieldContent";
      t16 = "form-company-type-select.tsx";
      t17 = fieldLabel;
      t14 = "relative";
      T1 = Select;
      t7 = isPending;
      t8 = resolvedOptions.map(_temp);
      if ($[46] !== field) {
        t9 = val => field.handleChange(val);
        $[46] = field;
        $[47] = t9;
      } else {
        t9 = $[47];
      }
      t10 = field.state.value;
      t11 = "Select";
      t12 = "form-company-type-select.tsx";
      const t25 = isCompact && "p-0 text-base data-[size=default]:h-6";
      let t26;
      if ($[48] !== t25) {
        t26 = cn("w-full justify-between gap-2", t25);
        $[48] = t25;
        $[49] = t26;
      } else {
        t26 = $[49];
      }
      const t27 = dataTestId ?? "form-company-type-select-trigger";
      let t28;
      if ($[50] !== placeholder) {
        t28 = <SelectValue placeholder={placeholder} data-sentry-element="SelectValue" data-sentry-source-file="form-company-type-select.tsx" />;
        $[50] = placeholder;
        $[51] = t28;
      } else {
        t28 = $[51];
      }
      let t29;
      if ($[52] !== isPending) {
        t29 = isPending && <Spinner className="text-muted-foreground ml-auto size-4" />;
        $[52] = isPending;
        $[53] = t29;
      } else {
        t29 = $[53];
      }
      if ($[54] !== field.name || $[55] !== isCompact || $[56] !== t26 || $[57] !== t27 || $[58] !== t28 || $[59] !== t29 || $[60] !== variant) {
        t13 = <SelectTrigger className={t26} data-testid={t27} hideChevron={isCompact} id={field.name} variant={variant} data-sentry-element="SelectTrigger" data-sentry-source-file="form-company-type-select.tsx">{t28}{t29}</SelectTrigger>;
        $[54] = field.name;
        $[55] = isCompact;
        $[56] = t26;
        $[57] = t27;
        $[58] = t28;
        $[59] = t29;
        $[60] = variant;
        $[61] = t13;
      } else {
        t13 = $[61];
      }
      T0 = SelectContent;
      t4 = "SelectContent";
      t5 = "form-company-type-select.tsx";
      t6 = resolvedOptions.map(_temp2);
    }
    $[0] = allOptions;
    $[1] = allowedValues;
    $[2] = dataTestId;
    $[3] = field;
    $[4] = isCompact;
    $[5] = isPending;
    $[6] = isReadOnly;
    $[7] = label;
    $[8] = placeholder;
    $[9] = required;
    $[10] = variant;
    $[11] = T0;
    $[12] = T1;
    $[13] = T2;
    $[14] = T3;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
    $[18] = t13;
    $[19] = t14;
    $[20] = t15;
    $[21] = t16;
    $[22] = t17;
    $[23] = t18;
    $[24] = t19;
    $[25] = t20;
    $[26] = t21;
    $[27] = t22;
    $[28] = t4;
    $[29] = t5;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
  } else {
    T0 = $[11];
    T1 = $[12];
    T2 = $[13];
    T3 = $[14];
    t10 = $[15];
    t11 = $[16];
    t12 = $[17];
    t13 = $[18];
    t14 = $[19];
    t15 = $[20];
    t16 = $[21];
    t17 = $[22];
    t18 = $[23];
    t19 = $[24];
    t20 = $[25];
    t21 = $[26];
    t22 = $[27];
    t4 = $[28];
    t5 = $[29];
    t6 = $[30];
    t7 = $[31];
    t8 = $[32];
    t9 = $[33];
  }
  if (t22 !== Symbol.for("react.early_return_sentinel")) {
    return t22;
  }
  let t23;
  if ($[62] !== T0 || $[63] !== t4 || $[64] !== t5 || $[65] !== t6) {
    t23 = <T0 data-sentry-element={t4} data-sentry-source-file={t5}>{t6}</T0>;
    $[62] = T0;
    $[63] = t4;
    $[64] = t5;
    $[65] = t6;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  let t24;
  if ($[67] !== T1 || $[68] !== t10 || $[69] !== t11 || $[70] !== t12 || $[71] !== t13 || $[72] !== t23 || $[73] !== t7 || $[74] !== t8 || $[75] !== t9) {
    t24 = <T1 disabled={t7} items={t8} onValueChange={t9} value={t10} data-sentry-element={t11} data-sentry-source-file={t12}>{t13}{t23}</T1>;
    $[67] = T1;
    $[68] = t10;
    $[69] = t11;
    $[70] = t12;
    $[71] = t13;
    $[72] = t23;
    $[73] = t7;
    $[74] = t8;
    $[75] = t9;
    $[76] = t24;
  } else {
    t24 = $[76];
  }
  let t25;
  if ($[77] !== t14 || $[78] !== t24) {
    t25 = <div className={t14}>{t24}</div>;
    $[77] = t14;
    $[78] = t24;
    $[79] = t25;
  } else {
    t25 = $[79];
  }
  let t26;
  if ($[80] !== description) {
    t26 = description && typeof description === "string" ? <FieldDescription>{description}</FieldDescription> : description;
    $[80] = description;
    $[81] = t26;
  } else {
    t26 = $[81];
  }
  let t27;
  if ($[82] !== T2 || $[83] !== t15 || $[84] !== t16 || $[85] !== t17 || $[86] !== t25 || $[87] !== t26) {
    t27 = <T2 data-sentry-element={t15} data-sentry-source-file={t16}>{t17}{t25}{t26}</T2>;
    $[82] = T2;
    $[83] = t15;
    $[84] = t16;
    $[85] = t17;
    $[86] = t25;
    $[87] = t26;
    $[88] = t27;
  } else {
    t27 = $[88];
  }
  let t28;
  if ($[89] !== field.state.meta.errors) {
    t28 = <FieldError className="text-sm" errors={field.state.meta.errors} data-sentry-element="FieldError" data-sentry-source-file="form-company-type-select.tsx" />;
    $[89] = field.state.meta.errors;
    $[90] = t28;
  } else {
    t28 = $[90];
  }
  let t29;
  if ($[91] !== T3 || $[92] !== t18 || $[93] !== t19 || $[94] !== t20 || $[95] !== t21 || $[96] !== t27 || $[97] !== t28) {
    t29 = <T3 className={t18} data-sentry-element={t19} data-sentry-component={t20} data-sentry-source-file={t21}>{t27}{t28}</T3>;
    $[91] = T3;
    $[92] = t18;
    $[93] = t19;
    $[94] = t20;
    $[95] = t21;
    $[96] = t27;
    $[97] = t28;
    $[98] = t29;
  } else {
    t29 = $[98];
  }
  return t29;
};
function _temp(option_1) {
  return {
    label: <CompanyTypeOptionLabel {...option_1} />,
    value: option_1.value
  };
}
function _temp2(type) {
  return <SelectItem data-testid={`form-company-type-select-option-${type.value}`} key={type.value} label={type.label} value={type.value}><CompanyTypeOptionLabel {...type} /></SelectItem>;
}